import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Globe, RefreshCw } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { getAdminAuthHeaders, useAdminAuth } from "./AdminAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_STYLES = {
  Operativo: "bg-emerald-100 text-emerald-700",
  Desactivado: "bg-slate-100 text-slate-600",
  Error: "bg-rose-100 text-rose-700",
  "Sin sincronizar": "bg-amber-100 text-amber-700",
};

export default function AdminExternalJobsPage() {
  const { user } = useAdminAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [togglingMaster, setTogglingMaster] = useState(false);
  const [togglingAdzuna, setTogglingAdzuna] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/external-jobs/status`, { headers: getAdminAuthHeaders() });
      setStatus(res.data);
    } catch {
      toast.error("No pudimos cargar el estado de fuentes externas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const toggleMaster = async (checked) => {
    setTogglingMaster(true);
    try {
      await axios.patch(`${API}/admin/external-jobs/settings/master`, { enabled: checked }, { headers: getAdminAuthHeaders() });
      toast.success(checked ? "Empleos externos activados" : "Empleos externos desactivados");
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar el interruptor maestro");
    } finally {
      setTogglingMaster(false);
    }
  };

  const toggleAdzuna = async (checked) => {
    setTogglingAdzuna(true);
    try {
      await axios.patch(`${API}/admin/external-jobs/settings/providers/adzuna`, { enabled: checked }, { headers: getAdminAuthHeaders() });
      toast.success(checked ? "Adzuna activado — sincronizando en segundo plano" : "Adzuna desactivado");
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar Adzuna");
    } finally {
      setTogglingAdzuna(false);
    }
  };

  const runManualSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/external-jobs/sync`, {}, { headers: getAdminAuthHeaders() });
      if (res.data.skipped) {
        toast.info(`Sincronización omitida: ${res.data.skip_reason}`);
      } else {
        toast.success(`Sincronización completada: ${res.data.jobs_created} nuevos, ${res.data.jobs_updated} actualizados`);
      }
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="external-jobs-admin-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const adzuna = status?.providers?.adzuna;
  const lastSync = adzuna?.syncs?.[0];
  const masterEnabled = status?.settings?.admin?.external_jobs_enabled;
  const adzunaEnabled = status?.settings?.admin?.adzuna_enabled;
  const envMasterOff = !status?.settings?.env?.external_jobs_enabled;
  const envAdzunaOff = !status?.settings?.env?.adzuna_enabled;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="admin-external-jobs-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Globe className="h-5 w-5 text-[#028CF8]" /> Fuentes externas de empleos</h1>
        <p className="text-sm text-slate-500 mt-1">Controla la publicación y sincronización de proveedores externos de inventario (ej. Adzuna) sin tocar código ni el entorno.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-slate-900">Interruptor maestro — Empleos externos</p>
            <p className="text-sm text-slate-500 mt-0.5">Controla la publicación y sincronización de todas las fuentes externas.</p>
            {envMasterOff && <p className="text-xs text-rose-600 mt-1" data-testid="external-jobs-env-master-warning">El flag de infraestructura (.env) está en OFF — este switch no tiene efecto hasta que se reactive a nivel de servidor.</p>}
          </div>
          <Switch
            checked={!!masterEnabled}
            disabled={!isSuperAdmin || togglingMaster}
            onCheckedChange={toggleMaster}
            data-testid="external-jobs-master-switch"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <p className="font-semibold text-slate-900 mb-3">Proveedores</p>
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-3">
            <span className="font-medium text-slate-800">Adzuna</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATE_STYLES[adzuna?.state] || "bg-slate-100 text-slate-600"}`} data-testid="adzuna-state-badge">
              {adzuna?.state || "Desconocido"}
            </span>
            {envAdzunaOff && <span className="text-xs text-rose-600">.env: OFF</span>}
          </div>
          <Switch
            checked={!!adzunaEnabled}
            disabled={!isSuperAdmin || togglingAdzuna}
            onCheckedChange={toggleAdzuna}
            data-testid="adzuna-provider-switch"
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100 text-sm">
          <div><p className="text-slate-400 text-xs">Empleos externos activos</p><p className="font-semibold text-slate-800" data-testid="adzuna-total-active">{lastSync?.total_active ?? "—"}</p></div>
          <div><p className="text-slate-400 text-xs">Última sincronización</p><p className="font-semibold text-slate-800">{lastSync?.last_sync ? new Date(lastSync.last_sync).toLocaleString() : "—"}</p></div>
          <div><p className="text-slate-400 text-xs">Próxima sincronización</p><p className="font-semibold text-slate-800">{adzuna?.next_sync ? new Date(adzuna.next_sync).toLocaleString() : "—"}</p></div>
          <div><p className="text-slate-400 text-xs">Último resultado</p><p className="font-semibold text-slate-800">{lastSync?.last_sync_status || "—"}</p></div>
          <div><p className="text-slate-400 text-xs">Nuevos / Actualizados</p><p className="font-semibold text-slate-800">{lastSync ? `${lastSync.jobs_created} / ${lastSync.jobs_updated}` : "—"}</p></div>
          <div><p className="text-slate-400 text-xs">Desactivados (expiración)</p><p className="font-semibold text-slate-800">{lastSync?.jobs_deactivated ?? "—"}</p></div>
        </div>

        {lastSync?.recent_errors?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="adzuna-recent-errors">
            <p className="text-xs text-slate-400 mb-1">Errores recientes</p>
            {lastSync.recent_errors.slice(-3).reverse().map((e, i) => (
              <p key={i} className="text-xs text-rose-600">{new Date(e.at).toLocaleString()} — {e.error}</p>
            ))}
          </div>
        )}

        <button
          onClick={runManualSync}
          disabled={syncing || !isSuperAdmin}
          data-testid="adzuna-manual-sync-button"
          className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#028CF8] hover:text-[#0270C5] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizar ahora
        </button>
      </div>

      {!isSuperAdmin && (
        <p className="text-xs text-slate-400" data-testid="external-jobs-readonly-note">Solo un super_admin puede modificar estos interruptores.</p>
      )}
    </div>
  );
}
